import { type ReactNode } from "react"
import { cn } from "@/lib/cn"
import { Skeleton } from "./Skeleton"

export type DataTableColumn<Row> = {
  id: string
  header: ReactNode
  cell: (row: Row) => ReactNode
  className?: string
}

export type DataTableProps<Row> = {
  columns: ReadonlyArray<DataTableColumn<Row>>
  rows: ReadonlyArray<Row>
  rowKey: (row: Row) => string
  loading?: boolean
  emptyState?: ReactNode
  className?: string
  caption?: ReactNode
}

/**
 * Skeleton estrutural — sem ordenação/paginação/seleção. Camadas de domínio
 * (filtros, server-side fetching) entram nos consumers, não aqui.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyState,
  className,
  caption,
}: DataTableProps<Row>) {
  return (
    <div
      className={cn(
        "w-full overflow-x-auto rounded-lg border border-markv-surface-200 bg-markv-surface-50",
        className,
      )}
    >
      <table className="w-full border-collapse text-left text-sm text-markv-text-700">
        {caption ? (
          <caption className="px-4 py-2 text-left text-xs text-markv-text-400">
            {caption}
          </caption>
        ) : null}
        <thead className="border-b border-markv-surface-200 bg-markv-surface-100">
          <tr>
            {columns.map((col) => (
              <th
                key={col.id}
                scope="col"
                className={cn("px-4 py-2 text-xs font-medium text-markv-text-500", col.className)}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <tr key={`skeleton-${i}`} className="border-b border-markv-surface-200 last:border-0">
                {columns.map((col) => (
                  <td key={col.id} className={cn("px-4 py-3", col.className)}>
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-sm text-markv-text-400"
              >
                {emptyState ?? "Sem registros."}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                className="border-b border-markv-surface-200 last:border-0 hover:bg-markv-surface-100"
              >
                {columns.map((col) => (
                  <td key={col.id} className={cn("px-4 py-3", col.className)}>
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
